import React from "react";

import {
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiCopy,
  FiEdit2,
  FiEye,
  FiFileText,
  FiPrinter,
  FiRepeat,
  FiTrash2,
  FiXCircle,
} from "react-icons/fi";

import "./QuotationTable.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getNestedValue = (
  object,
  path,
  fallback = ""
) => {
  if (!object || !path) {
    return fallback;
  }

  const value = String(path)
    .split(".")
    .reduce(
      (result, key) =>
        result?.[key],
      object
    );

  return value === undefined ||
    value === null
    ? fallback
    : value;
};

const getFirstValue = (
  object,
  paths = [],
  fallback = ""
) => {
  for (const path of paths) {
    const value =
      getNestedValue(
        object,
        path,
        undefined
      );

    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      return value;
    }
  }

  return fallback;
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
};

const formatCurrency = (
  value,
  currency = "INR"
) => {
  const amount =
    Number(value || 0);

  try {
    return new Intl.NumberFormat(
      "en-IN",
      {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }
    ).format(amount);
  } catch (error) {
    return `₹${amount.toLocaleString(
      "en-IN"
    )}`;
  }
};

const formatLabel = (value) => {
  if (!value) {
    return "-";
  }

  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
};

const getQuotationId = (
  quotation
) =>
  quotation?._id ||
  quotation?.id ||
  quotation?.quotationId ||
  "";

const getQuotationNumber = (
  quotation
) =>
  getFirstValue(
    quotation,
    [
      "documentNumber",
      "quotationNumber",
      "quotationNo",
      "number",
      "documentNo",
    ],
    "-"
  );

const getQuotationDate = (
  quotation
) =>
  getFirstValue(
    quotation,
    [
      "documentDate",
      "quotationDate",
      "date",
      "createdAt",
    ],
    ""
  );

const getValidUntil = (
  quotation
) =>
  getFirstValue(
    quotation,
    [
      "validUntil",
      "validTill",
      "expiryDate",
      "expirationDate",
    ],
    ""
  );

const getCustomerName = (
  quotation
) =>
  getFirstValue(
    quotation,
    [
      "customerSnapshot.customerName",
      "customerSnapshot.name",
      "customer.customerName",
      "customer.name",
      "customerName",
      "billingAddress.name",
    ],
    "Walk-in Customer"
  );

const getCustomerMobile = (
  quotation
) =>
  getFirstValue(
    quotation,
    [
      "customerSnapshot.mobile",
      "customerSnapshot.phone",
      "customer.mobile",
      "customer.phone",
      "customerMobile",
      "mobile",
    ],
    ""
  );

const getGrandTotal = (
  quotation
) =>
  getFirstValue(
    quotation,
    [
      "totals.grandTotal",
      "grandTotal",
      "totalAmount",
      "netAmount",
      "amount",
    ],
    0
  );

const getDocumentStatus = (
  quotation
) =>
  getFirstValue(
    quotation,
    [
      "documentStatus",
      "quotationStatus",
      "status",
    ],
    "DRAFT"
  );

const getConversionStatus = (
  quotation
) => {
  const explicitStatus =
    getFirstValue(
      quotation,
      [
        "conversionStatus",
        "conversion.status",
      ],
      ""
    );

  if (explicitStatus) {
    return explicitStatus;
  }

  /*
  |--------------------------------------------------------------------------
  | Backward compatibility
  |--------------------------------------------------------------------------
  | Older converted quotations may already contain convertedTo[] but were
  | created before conversionStatus existed in the Mongoose schema.
  */

  if (
    Array.isArray(
      quotation?.convertedTo
    ) &&
    quotation.convertedTo.length > 0
  ) {
    return "CONVERTED";
  }

  return "NOT_CONVERTED";
};

const getSalesChannel = (
  quotation
) =>
  getFirstValue(
    quotation,
    [
      "salesChannel",
      "channel",
      "sourceChannel",
    ],
    "-"
  );

const getStatusClass = (
  status
) => {
  switch (
    String(status || "")
      .toUpperCase()
  ) {
    case "CONFIRMED":
      return "quotation-status-confirmed";

    case "CANCELLED":
      return "quotation-status-cancelled";

    default:
      return "quotation-status-draft";
  }
};

const getConversionStatusClass = (
  status
) => {
  switch (
    String(status || "")
      .toUpperCase()
  ) {
    case "CONVERTED":
      return "quotation-conversion-converted";

    case "PARTIALLY_CONVERTED":
      return "quotation-conversion-partial";

    default:
      return "quotation-conversion-pending";
  }
};

const canEditQuotation = (
  quotation
) =>
  String(
    getDocumentStatus(
      quotation
    )
  ).toUpperCase() ===
  "DRAFT";

const canConfirmQuotation = (
  quotation
) =>
  String(
    getDocumentStatus(
      quotation
    )
  ).toUpperCase() ===
  "DRAFT";

const canCancelQuotation = (
  quotation
) =>
  String(
    getDocumentStatus(
      quotation
    )
  ).toUpperCase() ===
  "CONFIRMED";

const canConvertQuotation = (
  quotation
) =>
  String(
    getDocumentStatus(
      quotation
    )
  ).toUpperCase() ===
    "CONFIRMED" &&
  String(
    getConversionStatus(
      quotation
    )
  ).toUpperCase() !==
    "CONVERTED";

const canDeleteQuotation = (
  quotation
) =>
  String(
    getDocumentStatus(
      quotation
    )
  ).toUpperCase() ===
  "DRAFT";

/*
|--------------------------------------------------------------------------
| QUOTATION TABLE
|--------------------------------------------------------------------------
*/

const QuotationTable = ({
  quotations = [],
  loading = false,
  pagination,

  currency = "INR",

  onView,
  onEdit,
  onConfirm,
  onCancel,
  onDuplicate,
  onConvert,
  onPrint,
  onDelete,

  onPageChange,
  onLimitChange,
  onCreateQuotation,

  activeAction = null,
}) => {
  const safePagination = {
    page: Number(
      pagination?.page || 1
    ),

    limit: Number(
      pagination?.limit || 10
    ),

    total: Number(
      pagination?.total || 0
    ),

    totalPages: Math.max(
      Number(
        pagination?.totalPages ||
          1
      ),
      1
    ),
  };

  const safeQuotations =
    Array.isArray(quotations)
      ? quotations
      : [];

  const getSerialNumber = (
    index
  ) => {
    return (
      (
        safePagination.page -
        1
      ) *
        safePagination.limit +
      index +
      1
    );
  };

  const isActionLoading = (
    quotation,
    actionType
  ) => {
    return (
      String(
        activeAction?.id ||
          ""
      ) ===
        String(
          getQuotationId(
            quotation
          )
        ) &&
      activeAction?.type ===
        actionType
    );
  };

  const renderActionIcon = (
    quotation,
    actionType,
    Icon
  ) => {
    if (
      isActionLoading(
        quotation,
        actionType
      )
    ) {
      return (
        <span className="quotation-action-spinner" />
      );
    }

    return <Icon />;
  };

  return (
    <div className="quotation-table-card">
      <div className="quotation-table-scroll">
        <table className="quotation-table">
          <thead>
            <tr>
              <th style={{ width: 70 }}>
                S.No
              </th>

              <th>
                Quotation
              </th>

              <th>
                Customer
              </th>

              <th>
                Quotation Date
              </th>

              <th>
                Valid Until
              </th>

              <th>
                Channel
              </th>

              <th>
                Amount
              </th>

              <th>
                Status
              </th>

              <th>
                Conversion
              </th>

              <th style={{ width: 290 }}>
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={10}
                  className="quotation-table-message"
                >
                  <div className="quotation-table-loading">
                    <span className="quotation-table-spinner" />

                    Loading quotations...
                  </div>
                </td>
              </tr>
            ) : safeQuotations.length ===
              0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="quotation-table-message"
                >
                  <div className="quotation-table-empty">
                    <FiFileText />

                    <strong>
                      No Quotations Found
                    </strong>

                    <span>
                      Create your first quotation
                      to start managing customer
                      proposals.
                    </span>

                    {onCreateQuotation && (
                      <button
                        type="button"
                        onClick={
                          onCreateQuotation
                        }
                      >
                        Create Quotation
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              safeQuotations.map(
                (
                  quotation,
                  index
                ) => {
                  const quotationId =
                    getQuotationId(
                      quotation
                    );

                  const documentStatus =
                    getDocumentStatus(
                      quotation
                    );

                  const conversionStatus =
                    getConversionStatus(
                      quotation
                    );

                  return (
                    <tr
                      key={
                        quotationId ||
                        index
                      }
                    >
                      <td className="quotation-table-serial">
                        {getSerialNumber(
                          index
                        )}
                      </td>

                      <td>
                        <div className="quotation-table-document">
                          <div className="quotation-table-document-icon">
                            <FiFileText />
                          </div>

                          <div>
                            <strong
                              title={getQuotationNumber(
                                quotation
                              )}
                            >
                              {getQuotationNumber(
                                quotation
                              )}
                            </strong>

                            <span>
                              {quotationId
                                ? `ID: ${String(
                                    quotationId
                                  ).slice(
                                    -8
                                  )}`
                                : "Quotation"}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="quotation-table-customer">
                          <strong
                            title={getCustomerName(
                              quotation
                            )}
                          >
                            {getCustomerName(
                              quotation
                            )}
                          </strong>

                          <span>
                            {getCustomerMobile(
                              quotation
                            ) ||
                              "No mobile number"}
                          </span>
                        </div>
                      </td>

                      <td>
                        <span className="quotation-table-date">
                          {formatDate(
                            getQuotationDate(
                              quotation
                            )
                          )}
                        </span>
                      </td>

                      <td>
                        <span className="quotation-table-date">
                          {formatDate(
                            getValidUntil(
                              quotation
                            )
                          )}
                        </span>
                      </td>

                      <td>
                        <span className="quotation-channel">
                          {formatLabel(
                            getSalesChannel(
                              quotation
                            )
                          )}
                        </span>
                      </td>

                      <td>
                        <strong className="quotation-table-amount">
                          {formatCurrency(
                            getGrandTotal(
                              quotation
                            ),
                            currency
                          )}
                        </strong>
                      </td>

                      <td>
                        <span
                          className={`quotation-status ${getStatusClass(
                            documentStatus
                          )}`}
                        >
                          {formatLabel(
                            documentStatus
                          )}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`quotation-conversion-status ${getConversionStatusClass(
                            conversionStatus
                          )}`}
                        >
                          {formatLabel(
                            conversionStatus
                          )}
                        </span>
                      </td>

                      <td>
                        <div className="quotation-actions">
                          <button
                            type="button"
                            className="quotation-action quotation-action-view"
                            title="View quotation"
                            onClick={() =>
                              onView?.(
                                quotation
                              )
                            }
                            disabled={isActionLoading(
                              quotation,
                              "view"
                            )}
                          >
                            {renderActionIcon(
                              quotation,
                              "view",
                              FiEye
                            )}
                          </button>

                          {canEditQuotation(
                            quotation
                          ) && (
                            <button
                              type="button"
                              className="quotation-action quotation-action-edit"
                              title="Edit quotation"
                              onClick={() =>
                                onEdit?.(
                                  quotation
                                )
                              }
                              disabled={isActionLoading(
                                quotation,
                                "edit"
                              )}
                            >
                              {renderActionIcon(
                                quotation,
                                "edit",
                                FiEdit2
                              )}
                            </button>
                          )}

                          {canConfirmQuotation(
                            quotation
                          ) && (
                            <button
                              type="button"
                              className="quotation-action quotation-action-confirm"
                              title="Confirm quotation"
                              onClick={() =>
                                onConfirm?.(
                                  quotation
                                )
                              }
                              disabled={isActionLoading(
                                quotation,
                                "confirm"
                              )}
                            >
                              {renderActionIcon(
                                quotation,
                                "confirm",
                                FiCheckCircle
                              )}
                            </button>
                          )}

                          {canCancelQuotation(
                            quotation
                          ) && (
                            <button
                              type="button"
                              className="quotation-action quotation-action-cancel"
                              title="Cancel quotation"
                              onClick={() =>
                                onCancel?.(
                                  quotation
                                )
                              }
                              disabled={isActionLoading(
                                quotation,
                                "cancel"
                              )}
                            >
                              {renderActionIcon(
                                quotation,
                                "cancel",
                                FiXCircle
                              )}
                            </button>
                          )}

                          <button
                            type="button"
                            className="quotation-action quotation-action-duplicate"
                            title="Duplicate quotation"
                            onClick={() =>
                              onDuplicate?.(
                                quotation
                              )
                            }
                            disabled={isActionLoading(
                              quotation,
                              "duplicate"
                            )}
                          >
                            {renderActionIcon(
                              quotation,
                              "duplicate",
                              FiCopy
                            )}
                          </button>

                          {canConvertQuotation(
                            quotation
                          ) && (
                            <button
                              type="button"
                              className="quotation-action quotation-action-convert"
                              title="Convert quotation"
                              onClick={() =>
                                onConvert?.(
                                  quotation
                                )
                              }
                              disabled={isActionLoading(
                                quotation,
                                "convert"
                              )}
                            >
                              {renderActionIcon(
                                quotation,
                                "convert",
                                FiRepeat
                              )}
                            </button>
                          )}

                          <button
                            type="button"
                            className="quotation-action quotation-action-print"
                            title="Print quotation"
                            onClick={() =>
                              onPrint?.(
                                quotation
                              )
                            }
                            disabled={isActionLoading(
                              quotation,
                              "print"
                            )}
                          >
                            {renderActionIcon(
                              quotation,
                              "print",
                              FiPrinter
                            )}
                          </button>

                          {canDeleteQuotation(
                            quotation
                          ) && (
                            <button
                              type="button"
                              className="quotation-action quotation-action-delete"
                              title="Delete quotation"
                              onClick={() =>
                                onDelete?.(
                                  quotation
                                )
                              }
                              disabled={isActionLoading(
                                quotation,
                                "delete"
                              )}
                            >
                              {renderActionIcon(
                                quotation,
                                "delete",
                                FiTrash2
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }
              )
            )}
          </tbody>
        </table>
      </div>

      {!loading &&
      safePagination.total > 0 ? (
        <div className="quotation-pagination">
          <div className="quotation-pagination-summary">
            Showing{" "}
            <strong>
              {(safePagination.page -
                1) *
                safePagination.limit +
                1}
            </strong>{" "}
            to{" "}
            <strong>
              {Math.min(
                safePagination.page *
                  safePagination.limit,
                safePagination.total
              )}
            </strong>{" "}
            of{" "}
            <strong>
              {safePagination.total}
            </strong>{" "}
            quotations
          </div>

          <div className="quotation-pagination-controls">
            <select
              value={
                safePagination.limit
              }
              onChange={
                onLimitChange
              }
              aria-label="Quotations per page"
            >
              <option value={10}>
                10 / page
              </option>

              <option value={20}>
                20 / page
              </option>

              <option value={50}>
                50 / page
              </option>

              <option value={100}>
                100 / page
              </option>
            </select>

            <button
              type="button"
              disabled={
                safePagination.page <=
                1
              }
              onClick={() =>
                onPageChange?.(
                  safePagination.page -
                    1
                )
              }
              title="Previous page"
            >
              <FiChevronLeft />
            </button>

            <span>
              Page{" "}
              <strong>
                {safePagination.page}
              </strong>{" "}
              of{" "}
              <strong>
                {
                  safePagination.totalPages
                }
              </strong>
            </span>

            <button
              type="button"
              disabled={
                safePagination.page >=
                safePagination.totalPages
              }
              onClick={() =>
                onPageChange?.(
                  safePagination.page +
                    1
                )
              }
              title="Next page"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default QuotationTable;